import React, { useState, useEffect, useRef } from 'react';
import { Download, X, Smartphone, Monitor, Heart } from 'lucide-react';
import { usePWA } from '../hooks/usePWA';

const PWAInstallManager = () => {
  const { isInstallable, isInstalled, installApp } = usePWA();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [browserType, setBrowserType] = useState('unknown');
  const [deviceType, setDeviceType] = useState('unknown');
  const [userEngagement, setUserEngagement] = useState(0);
  const installPromptShown = useRef(false);

  useEffect(() => {
    detectEnvironment();
    trackUserEngagement();
  }, []);

  const detectEnvironment = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Detect browser
    if (userAgent.includes('edg/')) setBrowserType('edge');
    else if (userAgent.includes('chrome')) setBrowserType('chrome');
    else if (userAgent.includes('firefox')) setBrowserType('firefox');
    else if (userAgent.includes('safari')) setBrowserType('safari');
    else setBrowserType('other');
    
    // Detect device
    if (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
      setDeviceType('mobile');
    } else {
      setDeviceType('desktop');
    }
  };

  const trackUserEngagement = () => {
    let engagementScore = 0;
    
    // Track page views
    setTimeout(() => {
      engagementScore++;
      setUserEngagement(prev => prev + 1);
    }, 10000); // After 10 seconds
    
    // Show install prompt based on user engagement
    setTimeout(() => {
      if (userEngagement >= 2 && isInstallable && !isInstalled && !installPromptShown.current) {
        setShowInstallPrompt(true);
        installPromptShown.current = true;
      }
    }, 15000); // After 15 seconds of engagement
  };

  const handleInstallClick = async () => {
    console.log('PWAInstallManager: Install button clicked');
    const success = await installApp();
    if (success) {
      setShowInstallPrompt(false);
      
      // Analytics tracking
      if (window.gtag) {
        window.gtag('event', 'pwa_installed', {
          event_category: 'engagement',
          event_label: browserType + '_' + deviceType
        });
      }
    }
  };

  const showFallbackInstructions = () => {
    let instructions = '';
    
    if (browserType === 'safari' && deviceType === 'mobile') {
      instructions = 'Tap the Share button (square with arrow) at the bottom of the screen, then tap "Add to Home Screen"';
    } else if (browserType === 'chrome' && deviceType === 'mobile') {
      instructions = 'Tap the menu button (three dots) and select "Add to Home screen"';
    } else if (browserType === 'edge') {
      instructions = 'Click the menu button (three dots) and select "Apps" > "Install this site as an app"';
    } else if (browserType === 'chrome') {
      instructions = 'Click the install icon in the address bar or menu > "Install Shot Clock Dating..."';
    } else {
      instructions = 'Look for an "Install" or "Add to Home Screen" option in your browser menu';
    }
    
    window.alert(`To install Shot Clock Dating:\n\n${instructions}`);
  };

  const dismissPrompt = () => {
    setShowInstallPrompt(false);
    installPromptShown.current = true;
    
    // Show again after some time if user is still engaged
    setTimeout(() => {
      if (userEngagement >= 5 && !isInstalled) {
        installPromptShown.current = false;
      }
    }, 300000); // 5 minutes
  };

  // Don't render if already installed
  if (isInstalled) return null;

  return (
    <>
      {/* Custom Install Prompt Modal */}
      {showInstallPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 relative">
            <button
              onClick={dismissPrompt}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <Heart className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Install Shot Clock Dating
              </h3>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                Get instant access to AI-powered speed dating! Install our app for a faster, 
                more immersive experience with offline capabilities.
              </p>
              
              <div className="flex items-center justify-center space-x-6 mb-6 text-sm text-gray-500">
                <div className="flex items-center">
                  {deviceType === 'mobile' ? <Smartphone className="w-4 h-4 mr-1" /> : <Monitor className="w-4 h-4 mr-1" />}
                  <span>No app store needed</span>
                </div>
                <div className="flex items-center">
                  <Download className="w-4 h-4 mr-1" />
                  <span>Instant install</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={handleInstallClick}
                  className="w-full bg-gradient-to-r from-pink-500 to-rose-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-pink-600 hover:to-rose-700 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <Download className="w-5 h-5" />
                  <span>Install Now</span>
                </button>
                
                <button
                  onClick={dismissPrompt}
                  className="w-full text-gray-500 font-medium py-2 hover:text-gray-700 transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Floating Install Button (Always Available) */}
      {isInstallable && !showInstallPrompt && (
        <div className="fixed bottom-20 left-4 z-40">
          <button
            onClick={() => setShowInstallPrompt(true)}
            className="bg-gradient-to-r from-pink-500 to-rose-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center space-x-2 max-w-xs"
            title="Install Shot Clock Dating"
          >
            <Download className="w-5 h-5" />
            <span className="hidden sm:inline text-sm font-medium">Install App</span>
          </button>
        </div>
      )}
      
      {/* Browser-Specific Install Hint */}
      {!isInstallable && userEngagement >= 2 && !installPromptShown.current && (
        <div className="fixed bottom-4 left-4 right-4 bg-pink-50 border border-pink-200 rounded-lg p-3 z-40 max-w-md mx-auto">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Heart className="w-5 h-5 text-pink-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-pink-800 font-medium">
                Install Shot Clock Dating for a better experience!
              </p>
              <button
                onClick={showFallbackInstructions}
                className="text-xs text-pink-600 hover:text-pink-800 underline mt-1"
              >
                Show me how →
              </button>
            </div>
            <button
              onClick={() => setUserEngagement(0)}
              className="flex-shrink-0 text-pink-400 hover:text-pink-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallManager;